/**
 * Shared utility for resolving the API URL and language code dynamically
 * from the current browser URL path.
 *
 * Multi-language architecture:
 *   The admin SPA is deployed at https://xxxx.com/{language}/admin/
 *   The API for that language is at  https://xxxx.com/{language}/api/
 *
 * This module extracts the language code from the URL pathname and returns
 * the corresponding API URL. If no valid language prefix is found (e.g. in
 * local development), it falls back to the REACT_APP_API_URL env variable.
 *
 * Security:
 *   Only whitelisted language codes (zh-CN, zh-TW, ja, ko) are recognized.
 *   Arbitrary path manipulation cannot access unintended API endpoints.
 */

const SUPPORTED_LANGUAGES = ['zh-CN', 'zh-TW', 'ja', 'ko'];
const DEFAULT_LANGUAGE = 'zh-CN';

/**
 * Extract the current language code from the browser URL path.
 *
 * Examples:
 *   /zh-CN/admin/        → 'zh-CN'
 *   /zh-CN/admin/users   → 'zh-CN'
 *   /ja/admin/           → 'ja'
 *   /admin/              → null (falls back to env var)
 *
 * @returns {string|null} The language code or null if not found.
 */
export function getLanguageCode() {
  if (typeof window === 'undefined') {
    return null;
  }

  const match = window.location.pathname.match(/^\/(zh-CN|zh-TW|ja|ko)\//);
  return match ? match[1] : null;
}

/**
 * Get the API base URL for the current language context.
 *
 * In production, the URL is derived from the language prefix in the path:
 *   /zh-TW/admin/ → https://xxxx.com/zh-TW/api
 *
 * In local development (no language prefix), falls back to:
 *   REACT_APP_API_URL env variable (e.g. http://localhost:4001)
 *
 * @returns {string} The full API base URL (no trailing slash).
 */
export function getApiUrl() {
  try {
    const lang = getLanguageCode();
    if (lang) {
      return `${window.location.origin}/${lang}/api`;
    }
  } catch (_e) {
    // window is not available (e.g. server-side rendering or test env)
  }

  // Fallback for local development or non-prefixed environments
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  return `${window.location.origin}/${DEFAULT_LANGUAGE}/api`;
}

/**
 * Get the basename for react-router based on the current URL.
 *
 * This ensures client-side routing works correctly when the admin SPA
 * is deployed under a language-prefixed path.
 *
 * Examples:
 *   /zh-CN/admin/        → '/zh-CN/admin'
 *   /zh-CN/admin/users   → '/zh-CN/admin'
 *   /ja/admin/           → '/ja/admin'
 *
 * @returns {string} The basename for the router.
 */
export function getBasename() {
  try {
    if (typeof window !== 'undefined') {
      const match = window.location.pathname.match(/^\/(zh-CN|zh-TW|ja|ko)\/admin/);
      if (match) {
        return match[0];
      }
    }
  } catch (_e) {
    // window not available
  }

  return '/';
}
